import { ReactNode } from "react";
import clsx from "clsx";
import { indicizzaPerChiave, perStatoTollerante } from "../../utils/statoCanonico";

// Mappatura 1:1 con i valori del campo `stato` in DB (installation_schedule,
// attivazioni...). Tenerla qui evita che ogni pagina reinventi i colori.
//
// ATTENZIONE: i colori degli stati vivono in DUE posti — qui come classi
// Tailwind per i badge, e in `utils/statoColors.ts` come hex per grafici e
// mappa. Non è una duplicazione che si può togliere (Tailwind non genera
// classi da valori calcolati a runtime), quindi vanno cambiati INSIEME: un
// badge blu accanto a una fetta di torta arancione per lo stesso stato è
// peggio di due colori simili.
const STATO_STYLES: Record<string, string> = {
  "Da programmare": "bg-[#ef4444]/10 text-[#ef4444]",
  Programmata: "bg-[#2563eb]/10 text-[#1d4ed8]",
  "In corso": "bg-[#eab308]/10 text-[#a16207]",
  Effettuata: "bg-[#22c55e]/10 text-[#15803d]",
  Rinviata: "bg-[#6b7280]/10 text-[#6b7280]",
  Attiva: "bg-[#22c55e]/10 text-[#15803d]",
};

// Cercata per chiave normalizzata, non alla lettera: lo `stato` di un
// intervento arriva da Odoo e non combacia mai esattamente ("Programmato" per
// "Programmata", "Completato" per "Effettuata"). Vedi utils/statoCanonico.ts.
const STATO_STYLES_PER_CHIAVE = indicizzaPerChiave(STATO_STYLES);

export type BadgeColor = "primary" | "accent" | "neutral" | "progress" | "success" | "warning" | "danger";

const COLOR_STYLES: Record<BadgeColor, string> = {
  primary: "bg-primary/10 text-primary",
  accent: "bg-accent/10 text-accent",
  neutral: "bg-black/5 text-ink/70",
  // Lo stesso giallo di «In corso» qui sopra (issue #211, Stato lavori).
  progress: "bg-[#eab308]/10 text-[#a16207]",
  success: "bg-[#22c55e]/10 text-[#15803d]",
  warning: "bg-[#f97316]/10 text-[#c2410c]",
  danger: "bg-[#ef4444]/10 text-[#dc2626]",
};

interface BadgeProps {
  children: ReactNode;
  /** Se il testo coincide con uno stato noto (es. "Programmata"), applica il colore corrispondente. */
  stato?: string;
  /** In alternativa a `stato`, un colore generico per badge non legati a uno stato. */
  color?: BadgeColor;
  className?: string;
}

export function Badge({ children, stato, color = "neutral", className }: BadgeProps) {
  const statoStyle = perStatoTollerante(STATO_STYLES_PER_CHIAVE, stato);

  return (
    <span
      className={clsx(
        // `truncate` + `max-w-full`: in un contenitore stretto il testo si tronca
        // con "…" invece di sbordare oltre il bordo (il badge non va mai a capo).
        // Perché funzioni, il contenitore deve poter restringersi: se è un grid/
        // flex item serve `min-w-0` su di lui (vedi StatoCard).
        "inline-flex max-w-full items-center truncate rounded-full px-2.5 py-1 text-xs font-semibold whitespace-nowrap",
        statoStyle ?? COLOR_STYLES[color],
        className
      )}
    >
      {children}
    </span>
  );
}
